import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Check, Search, Loader2, UserX } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { trpc } from "@/trpc";
import UserContext from "@/contexts/UserContext";
import { isDataScopedEmployee } from "@/utils/permissions";
import LeadAvatar from "./LeadAvatar";

// Presentational employee list (search + recency sort + keyboard nav) used by
// AsesorControl and the tablero's asesor filter. Mount it only while its
// container (popover/sheet) is open — mounting triggers the employees fetch.
type Props = {
  merchantId: string;
  excludeEmployeeId?: string;
  onSelect: (employeeId: string) => void;
  isPending: boolean;
  pendingEmployeeId?: string;
  onEscape?: () => void;
};

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

const EmployeePicker = ({
  merchantId,
  excludeEmployeeId,
  onSelect,
  isPending,
  pendingEmployeeId,
  onEscape,
}: Props) => {
  const { roles } = useContext(UserContext);
  const canConfigureAsesores = !isDataScopedEmployee(roles);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const { data: employees, isLoading } = trpc.employees.list.useQuery(
    { merchantId },
    { enabled: Boolean(merchantId) }
  );

  const candidateRows = useMemo(() => {
    if (!employees) return [];
    const filtered = employees.filter(
      (employee) => employee.status === "active" && employee.id !== excludeEmployeeId
    );
    const sorted = filtered.slice().sort((a, b) => {
      const aTime = a.lastAssignedAt ? new Date(a.lastAssignedAt).getTime() : 0;
      const bTime = b.lastAssignedAt ? new Date(b.lastAssignedAt).getTime() : 0;
      return bTime - aTime;
    });
    if (!search.trim()) return sorted;
    const needle = normalize(search.trim());
    return sorted.filter((employee) => normalize(employee.name).includes(needle));
  }, [employees, excludeEmployeeId, search]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [search, employees]);

  useEffect(() => {
    const id = window.setTimeout(() => searchInputRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, []);

  const commitSelect = (employeeId: string) => {
    if (isPending) return;
    if (employeeId === excludeEmployeeId) return;
    onSelect(employeeId);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      onEscape?.();
      return;
    }
    if (candidateRows.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((index) => Math.min(index + 1, candidateRows.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const target = candidateRows[highlightedIndex];
      if (target) commitSelect(target.id);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
        <Input
          ref={searchInputRef}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Buscar asesor…"
          className="h-9 pl-9"
          aria-label="Buscar asesor"
        />
      </div>

      <div className="max-h-[320px] overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col gap-1.5 py-1">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : candidateRows.length === 0 ? (
          search.trim().length > 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">
              Sin coincidencias para «{search.trim()}»
            </p>
          ) : (
            <div className="flex flex-col items-center gap-3 px-2 py-6 text-center">
              <div className="flex size-10 items-center justify-center rounded-full bg-gray-100">
                <UserX className="size-5 text-gray-500" />
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold text-gray-700">
                  No hay otros asesores disponibles
                </p>
                <p className="text-xs text-gray-500">
                  Activa al menos un asesor adicional para poder reasignar este lead.
                </p>
              </div>
              {canConfigureAsesores && (
                <Link
                  to="/configuration/assignment"
                  className="text-sm font-medium text-palomma-primary hover:underline"
                  onClick={onEscape}
                >
                  Gestionar asesores
                </Link>
              )}
            </div>
          )
        ) : (
          <ul role="listbox" aria-label="Asesores disponibles" className="flex flex-col">
            {candidateRows.map((employee, index) => {
              const isHighlighted = index === highlightedIndex;
              const isRowPending = isPending && pendingEmployeeId === employee.id;
              return (
                <li key={employee.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isHighlighted}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onClick={() => commitSelect(employee.id)}
                    disabled={isPending}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-md px-2 py-2 text-left text-sm transition-colors",
                      isHighlighted ? "bg-gray-100" : "hover:bg-gray-50",
                      isPending && !isRowPending && "opacity-50"
                    )}
                  >
                    <LeadAvatar size="sm" name={employee.name} />
                    <span className="flex-1 truncate font-medium">{employee.name}</span>
                    {isRowPending && <Loader2 className="size-4 animate-spin text-palomma-primary" />}
                    {!isRowPending && employee.id === excludeEmployeeId && (
                      <Check className="size-4 text-palomma-primary" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default EmployeePicker;
